import { useState } from 'react'

import api from '../../../api/api'

import { useOfflineMode } from './useOfflineMode'

export const SCREENS = {
  HOME: 'HOME',
  CARRIER_SELECTION: 'CARRIER_SELECTION',
  DELIVERY_CODE_ENTRY: 'DELIVERY_CODE_ENTRY',
  PACKAGE_COUNT: 'PACKAGE_COUNT',
  RESIDENT_SELECTION: 'RESIDENT_SELECTION',
  PACKAGE_PHOTO: 'PACKAGE_PHOTO',
  ACCESS_CODE: 'ACCESS_CODE',
  DOOR_UNLOCKED: 'DOOR_UNLOCKED',
  ENTER_CODE: 'ENTER_CODE',
  WEEKLY_ACCESS_CODE: 'WEEKLY_ACCESS_CODE',
  ERROR: 'ERROR',
}

export const usePackageRoom = () => {
  const [currentScreen, setCurrentScreen] = useState(SCREENS.HOME)
  const [packageCount, setPackageCount] = useState(0)
  const [processedPackages, setProcessedPackages] = useState(0)
  const [packages, setPackages] = useState([])
  const [selectedResident, setSelectedResident] = useState(null)
  const [selectedCarrier, setSelectedCarrier] = useState(null)
  const [accessCode, setAccessCode] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const { isConnectionIssue, handleCreatePackagesSession, weeklyCode, cachedSettings } =
    useOfflineMode()
  const [startTime, setStartTime] = useState(null)

  const handleDeliverPackage = () => {
    setStartTime(Date.now())
    const useCarrierAccessCode = cachedSettings?.carrierAccessCodeEnabled === true
    if (useCarrierAccessCode) {
      setCurrentScreen(SCREENS.DELIVERY_CODE_ENTRY)
    } else {
      setCurrentScreen(SCREENS.CARRIER_SELECTION)
    }
  }

  const handleDeliveryCodeSubmit = (carrier) => {
    setSelectedCarrier(carrier)
    setCurrentScreen(SCREENS.PACKAGE_COUNT)
  }

  const handleCarrierSelected = (carrier) => {
    setSelectedCarrier(carrier)
    setCurrentScreen(SCREENS.PACKAGE_COUNT)
  }

  const handlePackageCountSelected = (count) => {
    setPackageCount(count)
    setCurrentScreen(SCREENS.RESIDENT_SELECTION)
  }
  const handleResidentSelected = (resident) => {
    setSelectedResident(resident)
    setCurrentScreen(SCREENS.PACKAGE_PHOTO)
  }

  const handleGetAccessCode = async (packages) => {
    try {
      setIsLoading(true)
      const payload = {
        packages,
        startTime,
        endTime: Date.now(),
      }

      await api.syncPackageSession(payload)
      setAccessCode(weeklyCode.toString())
      setCurrentScreen(SCREENS.ACCESS_CODE)
      setPackageCount(0)
      setProcessedPackages(0)
      setPackages([])
      setSelectedResident(null)
      setSelectedCarrier(null)
      setStartTime(null)
    } catch (error) {
      // Note: This is as hotfix and temporary handling (especially for Arrive Hollywood building)
      if (!!error?.response?.status && error.response.status === 400) {
        setCurrentScreen(SCREENS.ERROR)
      } else {
        setCurrentScreen(SCREENS.WEEKLY_ACCESS_CODE)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleGetOfflineAccessCode = (packages) => {
    const sessionData = {
      packages: packages.map((packageData) => ({
        clientId: packageData.clientId,
        fullName: packageData.fullName,
        apartment: packageData.apartment,
        image: null,
        carrier: packageData.carrier,
      })),
      startTime,
    }

    handleCreatePackagesSession(sessionData)

    setAccessCode(weeklyCode.toString())
    setCurrentScreen(SCREENS.ACCESS_CODE)
    setPackageCount(0)
    setProcessedPackages(0)
    setPackages([])
    setSelectedResident(null)
    setSelectedCarrier(null)
    setStartTime(null)
  }

  const handlePhotoTaken = (uploadedImageUrl) => {
    const newPackage = {
      image: uploadedImageUrl,
      clientId: selectedResident.id,
      fullName: selectedResident.fullName,
      apartment: selectedResident.apartment,
      carrier: selectedCarrier,
    }

    const updatedPackages = [...packages, newPackage]
    setPackages(updatedPackages)

    const newProcessedCount = processedPackages + 1

    if (newProcessedCount < packageCount) {
      setCurrentScreen(SCREENS.RESIDENT_SELECTION)
      setProcessedPackages(newProcessedCount)
      setSelectedResident(null)
      return
    }

    // Process packages
    if (isConnectionIssue) {
      handleGetOfflineAccessCode(updatedPackages)
    } else {
      handleGetAccessCode(updatedPackages)
    }
  }

  const handleAccessCodeDone = () => {
    setCurrentScreen(SCREENS.HOME)
  }

  const handleReset = () => {
    setCurrentScreen(SCREENS.HOME)
    setPackageCount(0)
    setProcessedPackages(0)
    setPackages([])
    setSelectedResident(null)
    setSelectedCarrier(null)
    setAccessCode(null)
  }

  const handleBack = () => {
    switch (currentScreen) {
      case SCREENS.CARRIER_SELECTION:
        setCurrentScreen(SCREENS.HOME)
        break
      case SCREENS.DELIVERY_CODE_ENTRY:
        setCurrentScreen(SCREENS.HOME)
        break
      case SCREENS.PACKAGE_COUNT:
        // Check which flow we're in based on settings
        if (cachedSettings?.carrierAccessCodeEnabled === true) {
          setCurrentScreen(SCREENS.DELIVERY_CODE_ENTRY)
        } else {
          setCurrentScreen(SCREENS.CARRIER_SELECTION)
        }
        break
      case SCREENS.RESIDENT_SELECTION:
        if (processedPackages === 0) {
          setCurrentScreen(SCREENS.PACKAGE_COUNT)
        } else {
          setCurrentScreen(SCREENS.PACKAGE_PHOTO)

          const previousPackage = packages[packages.length - 1]

          if (previousPackage) {
            const previousResident = {
              id: previousPackage.clientId,
              fullName: previousPackage.fullName,
              apartment: previousPackage.apartment,
            }
            setSelectedResident(previousResident)
          }

          setPackages(packages.slice(0, -1))
          setProcessedPackages(processedPackages - 1)
        }
        break
      case SCREENS.PACKAGE_PHOTO:
        setCurrentScreen(SCREENS.RESIDENT_SELECTION)
        break
      case SCREENS.ENTER_CODE:
        setCurrentScreen(SCREENS.HOME)
        break
      case SCREENS.WEEKLY_ACCESS_CODE:
        setCurrentScreen(SCREENS.HOME)
        break
      default:
        break
    }
  }

  const handleEnterCode = () => {
    setCurrentScreen(SCREENS.ENTER_CODE)
  }

  const handleVerificationSuccess = () => {
    setCurrentScreen(SCREENS.DOOR_UNLOCKED)
  }

  const handleShowWeeklyCode = () => {
    setCurrentScreen(SCREENS.WEEKLY_ACCESS_CODE)
  }

  const handleWeeklyCodeDone = () => {
    setCurrentScreen(SCREENS.HOME)
  }

  return {
    currentScreen,
    packageCount,
    processedPackages,
    packages,
    selectedResident,
    selectedCarrier,
    accessCode,
    weeklyCode,
    isLoading,
    handleDeliverPackage,
    handleDeliveryCodeSubmit,
    handleCarrierSelected,
    handlePackageCountSelected,
    handleResidentSelected,
    handlePhotoTaken,
    handleAccessCodeDone,
    handleReset,
    handleBack,
    handleEnterCode,
    handleVerificationSuccess,
    handleShowWeeklyCode,
    handleWeeklyCodeDone,
  }
}
